import {
  add,
  endOfDay,
  isValid,
  startOfDay,
  startOfMonth,
  startOfWeek,
  sub,
  toDate,
} from "date-fns";
import type { Duration } from "date-fns";
import { Temporal } from "@js-temporal/polyfill";
import { formatTimestamp } from "./format-timestamp";
import { getDisplayTimeZone } from "./timezone";

export type NullableDateInput = Date | string | number | null | undefined;

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export type DateRangeLike = DateRange | [NullableDateInput, NullableDateInput] | null | undefined;

const MONDAY_WEEK_OPTIONS = { weekStartsOn: 1 as const };

const RELATIVE_DURATION_BY_TOKEN: Record<string, Duration> = {
  "1h": { hours: 1 },
  "24h": { days: 1 },
  "7d": { days: 7 },
  "30d": { days: 30 },
};

export type RelativePresetToken = keyof typeof RELATIVE_DURATION_BY_TOKEN;
export type FixedRangePreset = "today" | "yesterday" | "this_week" | "last_week" | "this_month";

type DateRangeBuilder = (referenceDate: Date) => DateRange;

const PRESET_RANGE_BUILDERS: Record<FixedRangePreset, DateRangeBuilder> = {
  today: (referenceDate) => ({
    start: startOfDay(referenceDate),
    end: referenceDate,
  }),
  yesterday: (referenceDate) => {
    const dayStart = startOfDay(sub(referenceDate, { days: 1 }));
    return {
      start: dayStart,
      end: endOfDay(dayStart),
    };
  },
  this_week: (referenceDate) => ({
    start: startOfWeek(referenceDate, MONDAY_WEEK_OPTIONS),
    end: referenceDate,
  }),
  last_week: (referenceDate) => {
    const currentWeekStart = startOfWeek(referenceDate, MONDAY_WEEK_OPTIONS);
    const start = sub(currentWeekStart, { weeks: 1 });
    const end = endOfDay(add(start, { days: 6 }));
    return { start, end };
  },
  this_month: (referenceDate) => ({
    start: startOfMonth(referenceDate),
    end: referenceDate,
  }),
};

export const parseDateInput = (value: NullableDateInput): Date | null => {
  if (value == null) return null;

  const parsed = value instanceof Date ? new Date(value.getTime()) : toDate(value);
  return isValid(parsed) ? parsed : null;
};

export const formatIsoDate = (value: NullableDateInput): string | null => {
  const parsed = parseDateInput(value);
  return parsed ? parsed.toISOString() : null;
};

export const formatIsoDateAtEndOfDay = (value: NullableDateInput): string | null => {
  const parsed = parseDateInput(value);
  if (!parsed) return null;

  return endOfDay(parsed).toISOString();
};

export const normalizeDateRange = (range: DateRangeLike): DateRange => {
  if (!range) {
    return { start: null, end: null };
  }

  if (Array.isArray(range)) {
    const [start, end] = range;
    return {
      start: parseDateInput(start),
      end: parseDateInput(end),
    };
  }

  return {
    start: parseDateInput(range.start),
    end: parseDateInput(range.end),
  };
};

export const toRangeTuple = (range: DateRange): [Date | null, Date | null] => [range.start, range.end];

export const relativeStartByToken = (token: string, referenceDate = new Date()): Date | null => {
  const duration = RELATIVE_DURATION_BY_TOKEN[token];
  if (!duration) return null;

  return sub(referenceDate, duration);
};

export const deriveRelativeStartDate = relativeStartByToken;

export const dateRangeForPreset = (preset: FixedRangePreset, referenceDate = new Date()): DateRange => {
  const builder = PRESET_RANGE_BUILDERS[preset];
  return builder ? builder(new Date(referenceDate.getTime())) : { start: null, end: null };
};

export interface TimestampRangeParams {
  start_timestamp: string;
  end_timestamp: string;
}

export interface DayBounds extends TimestampRangeParams {
  key: string;
  label: string;
}

const DAY_LABEL_FORMAT: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" };

export const temporalToDate = (value: Temporal.ZonedDateTime): Date => new Date(value.toInstant().epochMilliseconds);

export const isValidDate = (value: unknown): value is Date => value instanceof Date && isValid(value);

export const normalizeDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? new Date(value.getTime()) : null;
  }

  if (typeof value === "string") {
    const normalized = value.replace(" ", "T");
    const parsed = toDate(normalized);
    return isValid(parsed) ? parsed : null;
  }

  if (typeof value === "number") {
    const parsed = toDate(value);
    return isValid(parsed) ? parsed : null;
  }

  return null;
};

export const toIsoDateTime = (date: Date | null | undefined): string | null => {
  if (!date || !isValid(date)) {
    return null;
  }

  return date.toISOString();
};

export const compareDates = (a: Date | null | undefined, b: Date | null | undefined): number => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a.getTime() - b.getTime();
};

export const compareDatesDesc = (a: Date | null | undefined, b: Date | null | undefined): number =>
  compareDates(b, a);

export const getDayBounds = (timestamp: Date): DayBounds => {
  const timeZone = getDisplayTimeZone();
  const zoned = Temporal.Instant.from(timestamp.toISOString()).toZonedDateTimeISO(timeZone);
  const dayStart = zoned.startOfDay();
  const dayEnd = dayStart.add({ days: 1 }).subtract({ nanoseconds: 1 });

  return {
    key: dayStart.toPlainDate().toString(),
    start_timestamp: dayStart.toInstant().toString(),
    end_timestamp: dayEnd.toInstant().toString(),
    label: formatTimestamp(temporalToDate(dayStart), { formatOptions: DAY_LABEL_FORMAT }),
  };
};

export const buildDayTimestampParams = (date: NullableDateInput): TimestampRangeParams | null => {
  const parsed = parseDateInput(date);
  if (!parsed) {
    return null;
  }

  const bounds = getDayBounds(parsed);

  return {
    start_timestamp: bounds.start_timestamp,
    end_timestamp: bounds.end_timestamp,
  };
};
